import React from 'react';
import cs from 'classnames';
import { useThrottleCallback } from '@react-hook/throttle';
import { injectV2 } from '../../../injectV2';
import { UiLoggerStore, WidgetClickedElement } from '../../../stores/UiLoggerStore';
import { ExperimentsStore } from '../../../../stores/ExperimentsStore';
import { isElementInView } from '../../../../utils/isElementInView';
import { useOnWindowResize } from '../../../hooks/useOnWindowResize';
import { useOnWindowScroll } from '../../../hooks/useOnWindowScroll';
import { usePrevious } from '../../../hooks/usePrevious';
import { WidgetType } from '../../../stores/TemplatesStore';
import { WithTranslations, withTranslations } from '../../../../utils/withTranslations';
import { TranslationKey } from '../../../../i18n/i18n';
import { widgetFeatureListConfig } from './widgetFeatureListConfig';
import { WidgetCarousel } from './WidgetCarousel/WidgetCarousel';
import { WidgetFeatureSlide } from './WidgetFeatureSlide/WidgetFeatureSlide';
import { WidgetCover } from './WidgetCover/WidgetCover';
import {
  WidgetSlideDirection,
  WidgetSlideDirectionButton,
} from './WidgetSlideDirectionButton/WidgetSlideDirectionButton';
import { WidgetCarouselDots } from './WidgetCarouselDots/WidgetCarouselDots';
import { widgetDataHooks } from './Widget.dataHooks';
import styles from './Widget.scss';

interface WidgetProps extends WithTranslations {
  type: WidgetType | null;
  dataHook?: string;
  uiLoggerStore: UiLoggerStore;
  experimentsStore: ExperimentsStore;
}

export interface WidgetFeature {
  id: string;
  title: TranslationKey;
  description: TranslationKey;
  imagePath: string;
}

const INITIAL_SELECTED_SLIDE_INDEX = 0;

const WidgetCmp: React.FC<WidgetProps> = ({ type, dataHook, uiLoggerStore, experimentsStore, t }) => {
  const [isImpressionPending, setImpressionPending] = React.useState<boolean>(false);
  const [selectedSlideIndex, selectSlideIndex] = React.useState<number>(INITIAL_SELECTED_SLIDE_INDEX);
  const [isCarouselAnimationDisabled, setCarouselAnimationDisabled] = React.useState<boolean>(false);
  const [hoverStartTime, setHoverStartTime] = React.useState<number>(null);
  const previousSelectedSlideIndex = usePrevious<number>(selectedSlideIndex);

  const hasColoredCover =
    experimentsStore.is('specs.funnel.TemplatesGalleryEnableWidget', 'ColoredCover') ||
    experimentsStore.is('specs.funnel.TemplatesGalleryEnableWidgetForLoggedInUsers', 'ColoredCover');
  const hasWidgetCover =
    hasColoredCover ||
    experimentsStore.is('specs.funnel.TemplatesGalleryEnableWidget', 'WithCover') ||
    experimentsStore.is('specs.funnel.TemplatesGalleryEnableWidgetForLoggedInUsers', 'WithCover');

  const currentWidgetConfig = widgetFeatureListConfig[type];
  const features = currentWidgetConfig?.features;
  const selectedFeatureIndex = selectedSlideIndex - (hasWidgetCover ? 1 : 0);
  const selectedFeature: WidgetFeature | null = features?.[selectedFeatureIndex] ?? null;
  const isCarouselSlidingBackward = previousSelectedSlideIndex > selectedSlideIndex;

  const widgetRef = React.useRef<HTMLLIElement>(null);

  React.useEffect(() => {
    if (isImpressionPending) {
      uiLoggerStore.logWidgetAboveTheFoldImpression();
    }
  }, [isImpressionPending, uiLoggerStore, uiLoggerStore.logWidgetAboveTheFoldImpression]);

  const reportIfVisibleToUser = React.useCallback(() => {
    if (!isImpressionPending && isElementInView(widgetRef.current)) {
      setImpressionPending(true);
    }
  }, [isImpressionPending]);

  const handleSelectedSlideChange = (updatedSlideIndex: number, isUserTriggered: boolean) => {
    selectSlideIndex(updatedSlideIndex);
    // only slide changes triggered directly by user should be animated
    // while slide changes caused by switching to different widget type should not be animated
    setCarouselAnimationDisabled(!isUserTriggered);
  };

  const previousWidgetType = usePrevious<string>(type);
  React.useEffect(() => {
    if (!previousWidgetType || previousWidgetType === type) {
      return;
    }

    handleSelectedSlideChange(INITIAL_SELECTED_SLIDE_INDEX, false);
  }, [previousWidgetType, type]);

  React.useEffect(() => {
    setImpressionPending(false);
  }, [type]);

  React.useEffect(() => {
    uiLoggerStore.logWidgetLoaded(type);
  }, [type, uiLoggerStore]);

  React.useEffect(() => {
    reportIfVisibleToUser();
  }, [reportIfVisibleToUser, type]);

  const throttleTimeout = 200;
  useOnWindowResize(useThrottleCallback(reportIfVisibleToUser, 1000 / throttleTimeout));
  useOnWindowScroll(useThrottleCallback(reportIfVisibleToUser, 1000 / throttleTimeout));

  if (!currentWidgetConfig) {
    return null;
  }

  const handleWidgetClick = () => uiLoggerStore.logWidgetActions('Thumbnail');

  const createElementClickHandler = (elementName: WidgetClickedElement) => (event: React.MouseEvent) => {
    event.stopPropagation();
    uiLoggerStore.logWidgetActions(elementName, selectedSlideIndex);
  };

  const totalSlidesCount = features.length + (hasWidgetCover ? 1 : 0);
  const isSlideBackwardAvailable = selectedSlideIndex > 0;
  const isSlideForwardAvailable = selectedSlideIndex < totalSlidesCount - 1;

  const handleClickSlideDirectionButton = (direction: WidgetSlideDirection, event: React.MouseEvent) => {
    event.stopPropagation();
    const updatedSlideIndex = selectedSlideIndex + (direction === WidgetSlideDirection.Forward ? 1 : -1);
    const elementName = direction === WidgetSlideDirection.Forward ? 'Forward Arrow' : 'Backward Arrow';
    handleSelectedSlideChange(updatedSlideIndex, true);
    uiLoggerStore.logWidgetActions(elementName, selectedSlideIndex, updatedSlideIndex);
  };

  const handleCarouselDotClick = (updatedSlideIndex: number, event: React.MouseEvent) => {
    event.stopPropagation();
    handleSelectedSlideChange(updatedSlideIndex, true);
    uiLoggerStore.logWidgetActions('Slider Dot', selectedSlideIndex, updatedSlideIndex);
  };

  const handleMouseLeave = () => {
    uiLoggerStore.logWidgetHover({
      widgetType: type,
      hoverTime: Date.now() - (hoverStartTime || 0),
    });
  };

  const { coverDescription, coverTitle, coverColor } = currentWidgetConfig;

  return (
    <li
      className={styles.container}
      data-hook={dataHook}
      ref={widgetRef}
      onMouseEnter={() => setHoverStartTime(Date.now())}
      onMouseLeave={handleMouseLeave}
    >
      <div
        className={cs(styles.widget, {
          [styles.whiteText]: hasColoredCover && !selectedFeature,
          [styles.widgetTextDisableAnimation]: isCarouselAnimationDisabled,
        })}
        onClick={handleWidgetClick}
      >
        {isSlideBackwardAvailable && (
          <WidgetSlideDirectionButton
            direction={WidgetSlideDirection.Backward}
            onClick={handleClickSlideDirectionButton}
            dataHook={widgetDataHooks.directionButton({ direction: WidgetSlideDirection.Backward })}
          />
        )}
        <div className={styles.box}>
          {hasColoredCover && (
            <div
              className={cs(styles.background, {
                [styles.backgroundShown]: !selectedFeature,
                [styles.backgroundDisableAnimation]: isCarouselAnimationDisabled,
              })}
              style={{ backgroundColor: coverColor }}
              data-hook={widgetDataHooks.background()}
            />
          )}
          <div className={styles.content}>
            <header
              className={cs(styles.header, {
                [styles.headerWhiteBackground]: !selectedFeature,
                [styles.headerColorDisableAnimation]: isCarouselAnimationDisabled,
              })}
              data-hook={widgetDataHooks.header()}
            >
              {t(currentWidgetConfig.header)}
            </header>
            <WidgetCarousel
              currentSlideId={selectedFeature?.id ?? 'cover'}
              isSlidingBackward={isCarouselSlidingBackward}
              isAnimationDisabled={isCarouselAnimationDisabled}
            >
              {selectedFeature ? (
                <WidgetFeatureSlide
                  feature={selectedFeature}
                  createElementClickHandler={createElementClickHandler}
                  dataHook={widgetDataHooks.featureSlide()}
                />
              ) : (
                <WidgetCover title={coverTitle} description={coverDescription} dataHook={widgetDataHooks.cover()} />
              )}
            </WidgetCarousel>
          </div>
        </div>
        <WidgetCarouselDots
          isWhiteActiveDot={!selectedFeature}
          dotsCount={totalSlidesCount}
          onDotClick={handleCarouselDotClick}
          activeDotIndex={selectedSlideIndex}
          dataHook={widgetDataHooks.carouselDots()}
        />
        {isSlideForwardAvailable && (
          <WidgetSlideDirectionButton
            direction={WidgetSlideDirection.Forward}
            onClick={handleClickSlideDirectionButton}
            dataHook={widgetDataHooks.directionButton({ direction: WidgetSlideDirection.Forward })}
          />
        )}
      </div>
    </li>
  );
};

export const Widget = withTranslations(injectV2('uiLoggerStore', 'experimentsStore')(WidgetCmp));
